import { defineStore } from 'pinia';
import { ref } from 'vue';
import { reqGetActiveBanners } from 'src/api/banner';
import { normalizeBanners } from 'src/composables/useBanners';
import type { Banner, BannerWebSocketPayload } from 'types/app/banner';

export const useBannerStore = defineStore('banner', () => {
  const liveBanners = ref<Banner[]>([]);
  const dismissedBannerIds = ref<string[]>([]);
  const isLoadingLiveBanners = ref(false);
  const hasLiveBannersError = ref(false);

  async function fetchActiveBanners() {
    isLoadingLiveBanners.value = true;
    hasLiveBannersError.value = false;

    try {
      const response = await reqGetActiveBanners();
      liveBanners.value = normalizeBanners(response);
    } catch (error) {
      hasLiveBannersError.value = true;
      liveBanners.value = [];
      console.error('Failed to load active banners', error);
    } finally {
      isLoadingLiveBanners.value = false;
    }
  }

  function dismissBanner(id: string) {
    if (dismissedBannerIds.value.includes(id)) {
      return;
    }

    dismissedBannerIds.value = [...dismissedBannerIds.value, id];
  }

  function handleWebSocketUpdate(payload: BannerWebSocketPayload) {
    const bannerId = String(payload.bannerId);

    if (payload.action === 'deleted') {
      liveBanners.value = liveBanners.value.filter((banner) => banner.id !== bannerId);
      return;
    }

    dismissedBannerIds.value = dismissedBannerIds.value.filter((id) => id !== bannerId);
    void fetchActiveBanners();
  }

  return {
    liveBanners,
    dismissedBannerIds,
    isLoadingLiveBanners,
    hasLiveBannersError,
    fetchActiveBanners,
    dismissBanner,
    handleWebSocketUpdate
  };
});
